const ACCOUNT_ID_PATTERN = /^[0-9]{1,20}$/u;
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/u;
const CONVERSATION_ID_PATTERN = /^[0-9]{1,20}$/u;

function hasOnlyKeys(value, required, optional = []) {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const allowed = new Set([...required, ...optional]);
  const keys = Reflect.ownKeys(value);
  return (
    keys.every(key => typeof key === 'string' && allowed.has(key)) &&
    required.every(key => Object.prototype.hasOwnProperty.call(value, key))
  );
}

export function normalizeAccountId(value) {
  const accountId = String(value ?? '');
  return ACCOUNT_ID_PATTERN.test(accountId) ? accountId : null;
}

export function validateHubOrigin(value) {
  if (typeof value !== 'string' || value.length === 0 || value.length > 2048) {
    return null;
  }

  let parsed;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }

  if (
    parsed.protocol !== 'https:' ||
    !parsed.hostname ||
    parsed.username ||
    parsed.password ||
    parsed.pathname !== '/' ||
    parsed.search ||
    parsed.hash
  ) {
    return null;
  }

  return parsed.origin;
}

export function crmWorkspaceUrl(hubOrigin) {
  const origin = validateHubOrigin(hubOrigin);
  return origin ? `${origin}/chatwoot/crm-workspace` : null;
}

export function crmConversationWorkspaceUrl(hubOrigin) {
  const origin = validateHubOrigin(hubOrigin);
  return origin ? origin + '/chatwoot/crm-conversation' : null;
}

export function isCrmReadyMessage(data) {
  return hasOnlyKeys(data, ['type']) && data.type === 'crm:ready';
}

export function isCrmContextMessage(data) {
  if (
    !hasOnlyKeys(
      data,
      ['type', 'accountId', 'conversationId'],
      ['contactId', 'agentId']
    ) ||
    data.type !== 'crm:context'
  ) {
    return false;
  }

  const accountId = normalizeAccountId(data.accountId);
  const conversationId = normalizeAccountId(data.conversationId);
  const contactId =
    data.contactId === undefined || data.contactId === null
      ? null
      : normalizeAccountId(data.contactId);
  const agentId =
    data.agentId === undefined || data.agentId === null
      ? null
      : normalizeAccountId(data.agentId);
  return (
    accountId !== null &&
    CONVERSATION_ID_PATTERN.test(conversationId ?? '') &&
    (data.contactId === undefined ||
      data.contactId === null ||
      contactId !== null) &&
    (data.agentId === undefined || data.agentId === null || agentId !== null)
  );
}

export function isTrustedCrmReadyMessage(
  event,
  expectedOrigin,
  expectedSource
) {
  const origin = validateHubOrigin(expectedOrigin);
  const trusted =
    origin &&
    event &&
    event.origin === origin &&
    event.source === expectedSource &&
    isCrmReadyMessage(event.data);
  return Boolean(trusted);
}

export function isTrustedCrmContextMessage(
  event,
  expectedOrigin,
  expectedSource
) {
  const origin = validateHubOrigin(expectedOrigin);
  return Boolean(
    origin &&
      event &&
      event.origin === origin &&
      event.source === expectedSource &&
      isCrmContextMessage(event.data)
  );
}

function expiresAtSeconds(value) {
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

export function normalizeCrmSession(value, accountId, expectedOrigin) {
  const normalizedAccountId = normalizeAccountId(accountId);
  const origin = validateHubOrigin(expectedOrigin);
  if (
    !normalizedAccountId ||
    !origin ||
    typeof value !== 'object' ||
    value === null
  ) {
    return null;
  }

  const token = value.token;
  const responseAccountId = normalizeAccountId(value.accountId);
  const responseOrigin = validateHubOrigin(value.hubOrigin);
  const expiresAt = expiresAtSeconds(value.expiresAt);
  if (
    typeof token !== 'string' ||
    !TOKEN_PATTERN.test(token) ||
    responseAccountId !== normalizedAccountId ||
    responseOrigin !== origin ||
    expiresAt === null
  ) {
    return null;
  }

  return {
    token,
    expiresAt: value.expiresAt,
    hubOrigin: origin,
    accountId: normalizedAccountId,
  };
}

export function isUsableCrmSession(
  value,
  accountId,
  expectedOrigin,
  nowSeconds = Math.floor(Date.now() / 1000)
) {
  const normalized = normalizeCrmSession(value, accountId, expectedOrigin);
  if (!normalized || !Number.isSafeInteger(nowSeconds)) return false;
  const expiresAt = expiresAtSeconds(normalized.expiresAt);
  return expiresAt !== null && expiresAt > nowSeconds;
}

export function buildCrmSessionMessage(
  value,
  accountId,
  expectedOrigin,
  nowSeconds
) {
  if (!isUsableCrmSession(value, accountId, expectedOrigin, nowSeconds))
    return null;

  const normalizedAccountId = normalizeAccountId(accountId);
  return {
    type: 'crm:session',
    token: value.token,
    expiresAt: value.expiresAt,
    accountId: normalizedAccountId,
  };
}
